import React from 'react';
import LegalShell from './LegalShell';
import { LEGAL_BUSINESS_NAME, LEGAL_PAN, LEGAL_GSTIN, LEGAL_ADDRESS } from '../utils/legalInfo';

const PrivacyPolicy = () => (
  <LegalShell title="Privacy Policy" lastUpdated="18 April 2026">
    <p>
      MusicSync ("<strong>we</strong>", "<strong>us</strong>", or "<strong>the Service</strong>") is a cloud-based music
      scheduling and playback platform operated from India. This Privacy Policy explains what information we collect, how
      we use it, and the choices you have. By using the Service you consent to the practices described below.
    </p>

    <h2>1. Who is the controller?</h2>
    <p>
      The controller of personal data processed through MusicSync is <strong>{LEGAL_BUSINESS_NAME}</strong>, based in India,
      operating this Service at
      <a href="mailto:admin@musicsync.net"> admin@musicsync.net</a>. For any privacy-related questions, you may contact us
      at that address.
    </p>
    {(LEGAL_PAN || LEGAL_GSTIN || LEGAL_ADDRESS) && (
      <ul>
        {LEGAL_PAN && <li><strong>PAN:</strong> {LEGAL_PAN}</li>}
        {LEGAL_GSTIN && <li><strong>GSTIN:</strong> {LEGAL_GSTIN}</li>}
        {LEGAL_ADDRESS && <li><strong>Registered address:</strong> {LEGAL_ADDRESS}</li>}
      </ul>
    )}

    <h2>2. What we collect</h2>
    <h3>2.1 Account and billing data</h3>
    <ul>
      <li>Company name, contact name, email, phone number, role (tenant admin / manager / kiosk)</li>
      <li>Encrypted password hash (we never store plaintext passwords)</li>
      <li>Razorpay payment identifiers, subscription status and timestamps (we <strong>do not</strong> store card numbers, CVV, or UPI PIN — those are handled by Razorpay)</li>
    </ul>
    <h3>2.2 Operational data</h3>
    <ul>
      <li>Store / kiosk metadata you create: names, locations, groupings, branding preferences</li>
      <li>Music-library metadata sourced from your connected Google Drive (file IDs, file names, mime types — never the audio content itself; audio streams directly from Drive to the kiosk)</li>
      <li>Playlists, dayparts, announcements, and play-history counters</li>
      <li>Kiosk heartbeats, current-track telemetry, and remote-command acknowledgments</li>
      <li>Feedback messages you submit in-app</li>
    </ul>
    <h3>2.3 Technical data</h3>
    <ul>
      <li>Browser user-agent, IP address (retained for auth rate-limiting and abuse prevention)</li>
      <li>HttpOnly session cookies and a short-lived JWT stored in your browser</li>
    </ul>

    <h2>3. How we use your data</h2>
    <ul>
      <li>To operate the Service (authentication, scheduling, kiosk sync, billing)</li>
      <li>To send transactional messages only (password-reset emails, webhook-triggered payment notifications, in-app alerts). <strong>We do not send marketing email.</strong></li>
      <li>To comply with our contractual, tax, and legal obligations</li>
      <li>To detect, investigate, and prevent fraud or abuse</li>
    </ul>

    <h2>4. Legal basis (DPDP Act 2023 &amp; GDPR)</h2>
    <p>
      We process your data on the basis of (a) <strong>contractual necessity</strong> — to deliver the service you signed
      up for; (b) your <strong>consent</strong> — for optional integrations such as Google Drive; and (c) our
      <strong> legitimate interests</strong> — in securing the Service and preventing abuse.
    </p>

    <h2>5. Sharing and sub-processors</h2>
    <p>We share data only with the minimum set of processors needed to deliver the Service:</p>
    <ul>
      <li><strong>Razorpay Software Private Limited</strong> — payments</li>
      <li><strong>MongoDB Inc.</strong> — hosted database storage</li>
      <li><strong>Google LLC</strong> — Google Drive API (only for tenants who explicitly connect a Drive account)</li>
      <li><strong>Namecheap / Private Email</strong> — transactional email delivery</li>
      <li><strong>Hosting and CDN providers</strong> — to serve the web application</li>
    </ul>
    <p>We never sell your data. We do not share data with advertisers.</p>

    <h2>6. Tenant isolation</h2>
    <p>
      MusicSync is a multi-tenant platform. All data you create is scoped to your <em>tenant_id</em> and cannot be
      accessed by other tenants or their users. Cross-tenant access is technically enforced on every API query.
    </p>

    <h2>7. Data retention</h2>
    <ul>
      <li>Active account data — retained for the life of your account.</li>
      <li>Expired / cancelled accounts — data retained for up to <strong>90 days</strong> to allow recovery; after that it is permanently deleted.</li>
      <li>Payment records are retained for <strong>7 years</strong> to comply with Indian tax and audit law.</li>
      <li>Password-reset tokens expire 1 hour after issue and are single-use.</li>
    </ul>

    <h2>8. Your rights</h2>
    <p>You may at any time:</p>
    <ul>
      <li>Access, correct, or export the data we hold about you</li>
      <li>Request deletion of your account and associated data</li>
      <li>Withdraw consent for optional integrations (e.g., disconnect Google Drive)</li>
      <li>Lodge a complaint with the Data Protection Board of India or your local supervisory authority</li>
    </ul>
    <p>Email <a href="mailto:admin@musicsync.net">admin@musicsync.net</a> to exercise any of these rights. We will respond within 30 days.</p>

    <h2>9. Security</h2>
    <p>
      We use TLS in transit, bcrypt password hashing, HttpOnly + Secure cookies, scoped JWTs, role-based access control, and
      routine security monitoring. No system is perfectly secure — please keep your account password confidential.
    </p>

    <h2>10. Children</h2>
    <p>MusicSync is a B2B service and is not directed at individuals under 18. We do not knowingly collect data from minors.</p>

    <h2>11. Changes to this policy</h2>
    <p>We will post any material change to this policy on this page and notify tenant admins in-app. Continued use of the Service after an update constitutes acceptance.</p>

    <h2>12. Contact</h2>
    <p>
      Privacy questions: <a href="mailto:admin@musicsync.net">admin@musicsync.net</a>.
    </p>
  </LegalShell>
);

export default PrivacyPolicy;
